// api.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase"; // Ajustar path según tu configuración

const FUNCTIONS_REGION = "us-central1"; // Fixed region for UID consistency

export async function startWaSession({ numeroId, organizationId = null }) {
  try {
    const functions = getFunctions(app, FUNCTIONS_REGION);

    // Log current auth state for debugging
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth(app);
    console.log('🔐 auth.uid =', auth.currentUser?.uid);
    console.log('🌎 functions.region = us-central1');
    console.log("🔐 startWaSession(client) -> numeroId:", numeroId, "orgId:", organizationId);

    const fn = httpsCallable(functions, "startWaSession");
    // Enviar organizationId para que backend sepa la ruta correcta
    const result = await fn({ numeroId, organizationId });
    
    console.log('✅ startWaSession requestId:', result.data?.requestId);
    return result.data;
  } catch (error) {
    console.error("Error en startWaSession:", error);
    
    // Agregar información de diagnóstico
    if (error.code === 'functions/unauthenticated') {
      throw new Error("Usuario no autenticado. Inicia sesión e intenta de nuevo.");
    } else if (error.code === 'functions/not-found') {
      throw new Error("Función no encontrada. Verifica la región y el nombre.");
    } else if (error.code === 'functions/permission-denied') {
      throw new Error("Sin permisos. Verifica tu sesión y permisos.");
    }
    
    throw error;
  }
}

export async function closeWaSession({ numeroId, organizationId = null }) {
  try {
    const functions = getFunctions(app, FUNCTIONS_REGION);

    // Log current auth state for debugging
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth(app);
    console.log('🔐 closeWaSession: auth.uid=', auth.currentUser?.uid);
    console.log("🔒 closeWaSession(client) -> numeroId:", numeroId, "orgId:", organizationId);

    const fn = httpsCallable(functions, "closeWaSession");
    // Enviar organizationId para que backend sepa la ruta correcta
    const result = await fn({ numeroId, organizationId });

    console.log('✅ closeWaSession requestId:', result.data?.requestId);
    return result.data;
  } catch (error) {
    console.error("Error en closeWaSession:", error);

    // Agregar información de diagnóstico
    if (error.code === 'functions/unauthenticated') {
      throw new Error("Usuario no autenticado. Inicia sesión e intenta de nuevo.");
    } else if (error.code === 'functions/not-found') {
      throw new Error("Función no encontrada. Verifica la región y el nombre.");
    } else if (error.code === 'functions/permission-denied') {
      throw new Error("Sin permisos. Verifica tu sesión y permisos.");
    }

    throw error;
  }
}

// WhatsApp API (Meta Cloud API) methods
const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || "https://us-central1-sistema-bks.cloudfunctions.net";

// Export API_URL for use in other components
export { API_URL };

export const whatsappApi = {
  /**
   * Connect a WhatsApp number via Meta Cloud API
   * @param {Object} params
   * @param {string} params.accessToken - Meta access token
   * @param {string} params.phoneNumberId - Meta phone number ID
   * @param {string} params.wabaId - WhatsApp Business Account ID
   * @returns {Promise<Object>} Connection result
   */
  connect: async ({ accessToken, phoneNumberId, wabaId }) => {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth(app);

      if (!auth.currentUser) {
        throw new Error('Usuario no autenticado');
      }

      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(`${API_URL}/integrations_whatsappapi_connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          accessToken: accessToken.trim(),
          phoneNumberId: phoneNumberId.trim(),
          wabaId: wabaId.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al conectar WhatsApp API');
      }

      console.log('✅ WhatsApp API conectado:', data);
      return data;
    } catch (error) {
      console.error('Error en whatsappApi.connect:', error);
      throw error;
    }
  },

  /**
   * Disconnect a WhatsApp API number
   * @param {Object} params
   * @param {string} params.numeroId - Document ID in Firestore
   * @param {string} params.phoneNumberId - Meta phone number ID
   * @returns {Promise<Object>} Disconnection result
   */
  disconnect: async ({ numeroId, phoneNumberId, organizationId }) => {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth(app);

      if (!auth.currentUser) {
        throw new Error('Usuario no autenticado');
      }

      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(`${API_URL}/integrations_whatsappapi_disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          numeroId,
          phoneNumberId,
          organizationId: organizationId || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al desconectar WhatsApp API');
      }

      console.log('✅ WhatsApp API desconectado:', numeroId);
      return data;
    } catch (error) {
      console.error('Error en whatsappApi.disconnect:', error);
      throw error;
    }
  }
};